import { useState } from 'react';
import type { JSX } from 'react';

import { SectionBlock } from 'shared/ui';
import type { SnackbarVariant } from 'shared/ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { useCreateMappingsMutation, useDeleteMappingMutation, useUpdateMappingMutation } from '../api/integrations';
import { MAPPING_DIRECTIONS, PROVIDER_CATALOG } from '../model/integration';
import type { IntegrationDetail, IntegrationMapping, MappingBody, MappingDirection } from '../model/integration';
import FormDialog, { Field } from './FormDialog';

const DIRECTION_LABELS: Record<MappingDirection, string> = {
  both: 'Ambas (both)',
  in: 'Solo entrada (in)',
  out: 'Solo salida (out)',
};

// Both arrows point right; the dot is where they end ("in") or start ("out"), and "both" goes either way
const DirectionIcon = ({ direction }: { direction: MappingDirection }): JSX.Element => (
  <svg
    width="28"
    height="12"
    viewBox="0 0 28 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    // Inline size: the select item forces 16px on any svg without a size- class, which would squash it
    style={{ width: 28, height: 12 }}
    className="shrink-0 text-text-muted"
  >
    {direction === 'both' ? (
      <>
        <line x1="4" y1="6" x2="24" y2="6" />
        <polyline points="8,2 4,6 8,10" />
        <polyline points="20,2 24,6 20,10" />
      </>
    ) : direction === 'in' ? (
      <>
        <line x1="3" y1="6" x2="21" y2="6" />
        <polyline points="17,2 21,6 17,10" />
        <circle cx="25" cy="6" r="2.5" fill="currentColor" stroke="none" />
      </>
    ) : (
      <>
        <circle cx="3" cy="6" r="2.5" fill="currentColor" stroke="none" />
        <line x1="5" y1="6" x2="25" y2="6" />
        <polyline points="21,2 25,6 21,10" />
      </>
    )}
  </svg>
);

const DirectionLabel = ({ direction }: { direction: MappingDirection }): JSX.Element => (
  <span className="inline-flex items-center gap-2">
    <DirectionIcon direction={direction} />
    {DIRECTION_LABELS[direction]}
  </span>
);

export interface MappableProject {
  id: number;
  name: string;
}

type DialogState = { mode: 'create' } | { mode: 'edit' | 'delete'; mapping: IntegrationMapping };

interface MappingDialogProps {
  integration: IntegrationDetail;
  projects: MappableProject[];
  state: DialogState;
  onClose: () => void;
  notify: (message: string, variant?: SnackbarVariant) => void;
}

const MappingDialog = ({ integration, projects, state, onClose, notify }: MappingDialogProps): JSX.Element => {
  const kinds = PROVIDER_CATALOG[integration.provider]?.mappingKinds ?? [];
  const mapping = state.mode === 'create' ? null : state.mapping;
  const [kind, setKind] = useState(mapping?.kind ?? kinds[0] ?? '');
  const [lidzProjectId, setLidzProjectId] = useState(mapping?.lidzProjectId.toString() ?? '');
  const [externalId, setExternalId] = useState(mapping?.externalId ?? '');
  const [direction, setDirection] = useState<MappingDirection>(mapping?.direction ?? 'both');
  const [note, setNote] = useState(mapping?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  // A mapping saved before the master's subprojects changed keeps its project selectable, so the Select isn't blank
  const projectOptions =
    mapping && !projects.some(({ id }) => id === mapping.lidzProjectId)
      ? [...projects, { id: mapping.lidzProjectId, name: 'Proyecto fuera del master' }]
      : projects;

  const [createMappings, { isLoading: isCreating }] = useCreateMappingsMutation();
  const [updateMapping, { isLoading: isUpdating }] = useUpdateMappingMutation();
  const [deleteMapping, { isLoading: isDeleting }] = useDeleteMappingMutation();

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    if (state.mode !== 'delete' && !lidzProjectId) {
      setError('Elige un proyecto de Lidz');
      return;
    }
    const body = {
      kind,
      lidzProjectId: Number(lidzProjectId),
      externalId: externalId.trim(),
      direction,
      note: note.trim() || null,
    };
    try {
      if (state.mode === 'create') {
        await createMappings({ integrationId: integration.id, mappings: [body] }).unwrap();
        notify('Mapeo creado');
      } else if (state.mode === 'edit') {
        // Only what changed: the API re-checks lidzProjectId against the master, which an untouched old mapping may fail
        const changes = Object.fromEntries(
          Object.entries(body).filter(([key, value]) => state.mapping[key as keyof MappingBody] !== value)
        ) as Partial<MappingBody>;
        if (Object.keys(changes).length > 0) {
          await updateMapping({ id: state.mapping.id, integrationId: integration.id, body: changes }).unwrap();
          notify('Mapeo actualizado');
        }
      } else {
        await deleteMapping({ id: state.mapping.id, integrationId: integration.id }).unwrap();
        notify('Mapeo eliminado');
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo guardar el mapeo'));
    }
  };

  const isSaving = isCreating || isUpdating || isDeleting;

  if (state.mode === 'delete') {
    return (
      <FormDialog
        title="Eliminar mapeo"
        description={
          <>
            Se elimina el mapeo <span className="font-mono">{state.mapping.externalId}</span>. Los procesos dejan de
            reconocer ese ID en la próxima ejecución.
          </>
        }
        submitLabel="Eliminar"
        destructive
        isSaving={isSaving}
        error={error}
        onClose={onClose}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <FormDialog
      title={state.mode === 'create' ? 'Nuevo mapeo' : `Editar mapeo #${state.mapping.id}`}
      submitLabel={state.mode === 'create' ? 'Crear' : 'Guardar'}
      isSaving={isSaving}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <Field label="Tipo (kind)">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {kinds.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Proyecto Lidz" hint="Un subproyecto del master, o el master si no tiene subproyectos.">
        <Select value={lidzProjectId} onValueChange={setLidzProjectId}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Elige un proyecto" />
          </SelectTrigger>
          <SelectContent>
            {projectOptions.map((project) => (
              <SelectItem key={project.id} value={project.id.toString()}>
                {project.name} (#{project.id})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="ID externo" htmlFor="mapping-external-id" hint="Exactamente como lo usa el sistema externo.">
        <Input id="mapping-external-id" value={externalId} onChange={(e) => setExternalId(e.target.value)} required />
      </Field>
      <Field label="Dirección">
        <Select value={direction} onValueChange={(value) => setDirection(value as MappingDirection)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MAPPING_DIRECTIONS.map((value) => (
              <SelectItem key={value} value={value}>
                <DirectionLabel direction={value} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Nota" htmlFor="mapping-note">
        <Input id="mapping-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </FormDialog>
  );
};

interface MappingsSectionProps {
  integration: IntegrationDetail;
  projects: MappableProject[];
  notify: (message: string, variant?: SnackbarVariant) => void;
}

const MappingsSection = ({ integration, projects, notify }: MappingsSectionProps): JSX.Element => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const projectNames = new Map(projects.map(({ id, name }) => [id, name]));
  const mappings = [...integration.mappings].sort(
    (a, b) => a.kind.localeCompare(b.kind) || a.lidzProjectId - b.lidzProjectId
  );

  return (
    <SectionBlock
      title={`Mapeos (${mappings.length})`}
      action={
        <Button size="sm" variant="outline" onClick={() => setDialog({ mode: 'create' })}>
          Agregar mapeo
        </Button>
      }
    >
      {mappings.length === 0 ? (
        <p className="m-0 text-sm text-text-muted">Sin mapeos.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kind</TableHead>
              <TableHead>Proyecto Lidz</TableHead>
              <TableHead>ID externo</TableHead>
              <TableHead>Dirección</TableHead>
              <TableHead>Nota</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappings.map((mapping) => (
              <TableRow key={mapping.id}>
                <TableCell className="font-mono text-xs">{mapping.kind}</TableCell>
                <TableCell>
                  {projectNames.get(mapping.lidzProjectId) ?? 'Proyecto fuera del master'}{' '}
                  <span className="text-xs text-text-muted">#{mapping.lidzProjectId}</span>
                </TableCell>
                <TableCell className="font-mono text-xs">{mapping.externalId}</TableCell>
                <TableCell className="text-xs">
                  <DirectionLabel direction={mapping.direction} />
                </TableCell>
                <TableCell className="text-xs text-text-muted">{mapping.note ?? '—'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDialog({ mode: 'edit', mapping })}>
                      Editar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDialog({ mode: 'delete', mapping })}>
                      Eliminar
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {dialog ? (
        <MappingDialog
          integration={integration}
          projects={projects}
          state={dialog}
          onClose={() => setDialog(null)}
          notify={notify}
        />
      ) : null}
    </SectionBlock>
  );
};

export default MappingsSection;
